import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  FormContainer,
  FormInput,
  ISelectType,
  SwitchInput,
  useFormContext,
} from "@4uhub/lib4uhub";
import { useLocation, useParams } from "react-router";
import MainApi from "../../../services/mainApi.service";
import { useAppSelector } from "../../../store/store";
import { genericMemo } from "../../../components/SubFormArray/genericMemo";
import { IRelationShipFormScreenProps } from "./models";
import { uBusRelationshipTypeList } from "../../../services/uBusRelationshipType.service";
import { useEffect, useMemo } from "react";

const translationPath = "page.4uBus.relationships.defaultRelationships.";

const RELATIONSHIP_TYPE_ROUTE =
  process.env.REACT_APP_4UBUS + "/api/v1/SetRelationshipType/";
const relationshipTypeService = new MainApi<ISelectType>(
  RELATIONSHIP_TYPE_ROUTE,
);

const GENERIC_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Generic/";
const genericService = new MainApi<ISelectType>(GENERIC_ROUTE);

const FormFieldsInternal = ({
  isTag,
  relationshipItemParams,
  relationshipParams,
  t,
}: any) => {
  const { watch, setValue } = useFormContext();

  // Monitora o switch 'default'
  const isDefaultSelected = watch("default");

  // Injeta o '*' se o default for ativado
  useEffect(() => {
    if (isDefaultSelected) {
      setValue("originValue", "*");
    }
  }, [isDefaultSelected, setValue]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} textAlign={"end"}>
        <SwitchInput
          name="default"
          label={t(translationPath + "default") || "Default"}
        />
        <SwitchInput
          name="enable"
          label={t(translationPath + "enable") || "Enable"}
        />
      </Grid>

      {isTag && (
        <Grid item xs={12} sm={12}>
          <AutoComplete
            params={relationshipItemParams}
            name="uBusRelationshipType"
            label={t(translationPath + "relationship_type")}
            getOptionLabel={(option) => option.name}
            size="small"
            request={uBusRelationshipTypeList}
          />
        </Grid>
      )}

      {!isTag && (
        <>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              params={relationshipParams}
              name="setRelationshipType"
              label={t(translationPath + "relationship_type")}
              getOptionLabel={(option) => option.name}
              size="small"
              request={relationshipTypeService.list}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <AutoComplete
              params={{ Identifier: "DIRECTION_TYPE" }}
              name="directionType"
              label={t(translationPath + "direction_type")}
              getOptionLabel={(option) => option.name}
              size="small"
              request={genericService.list}
            />
          </Grid>
        </>
      )}

      <Grid item xs={12} sm={6}>
        <FormInput
          size="small"
          name="originName"
          fullWidth
          label={t(translationPath + "origin_name") || "Origin Name"}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormInput
          size="small"
          name="originValue"
          fullWidth
          label={t(translationPath + "origin_value") || "Origin Value"}
          disabled={isDefaultSelected} // <--- BLOQUEIA O CAMPO AQUI
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormInput
          size="small"
          name="destinyName"
          fullWidth
          label={t(translationPath + "destiny_name") || "Destiny Name"}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormInput
          size="small"
          name="destinyValue"
          fullWidth
          label={t(translationPath + "destiny_value") || "Destiny Value"}
        />
      </Grid>
    </Grid>
  );
};

export const RelationShipFormScreen = genericMemo(
  <T extends object>({
    id,
    isTag,
    loading,
    schema,
    subtitleWatchField,
    uBusAutomationCode,
    saveHandler,
    updateHandler,
    getHandler,
    onBackButton,
  }: IRelationShipFormScreenProps<T>) => {
    const { t } = useTranslation();

    const { pathname } = useLocation();

    const currentPage = useAppSelector((state) =>
      state.menu.items.find(
        (menu) =>
          menu.link ===
          `/${pathname.split("/").slice(4).slice(0, 3).join("/")}`,
      ),
    );

    const { extension } = useParams();

    const title = `${t(translationPath + "relationship")} ${
      currentPage ? t(currentPage.name) : ""
    }`;

    const relationshipItemParams = useMemo(
      () => ({
        _4uBusAutomationCode: uBusAutomationCode,
      }),
      [uBusAutomationCode],
    );

    const relationshipParams = useMemo(
      () => ({
        ExtensionCode: extension,
      }),
      [extension],
    );

    return (
      <FormContainer<T>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={title}
        subtitle={t(translationPath + "new_relationship")}
        backButtonOnClick={onBackButton}
        redirectOnSave={isTag ? false : true}
        onSuccess={onBackButton}
        isModalForm={isTag ? true : false}
        propId={id}
        formActionsOnBottom={isTag ? false : true}
        schema={schema}
        subtitleWatchField={subtitleWatchField}
        fields={() => (
          <FormFieldsInternal
            isTag={isTag}
            relationshipItemParams={relationshipItemParams}
            relationshipParams={relationshipParams}
            t={t}
          />
        )}
      />
    );
  },
);
